import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, AuthError, Session } from '@supabase/supabase-js';
import { directAuth, directSupabase } from '../lib/supabaseClient';
import { clearAllCaches } from '../hooks/useOutlookCalendar';

interface SupabaseAuthContextType {
  user: User | null;
  loading: boolean;
  error: AuthError | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
}

const SupabaseAuthContext = createContext<SupabaseAuthContextType | undefined>(undefined);

export const SupabaseAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);

  useEffect(() => {
    // Check active sessions and sets the user
    const initializeAuth = async () => {
      try {
        const sessionData = await directAuth.getSession();
        if (sessionData?.session) {
          setUser(sessionData.session.user);
        } else {
          setUser(null);
        }
      } catch (err) {
        console.log('Session initialization error:', err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    // Set up auth state listener for real-time session changes
    const { data: { subscription } } = directSupabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          setUser(session?.user || null);
          setLoading(false);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setLoading(false);
        } else if (event === 'INITIAL_SESSION') {
          setUser(session?.user || null);
          setLoading(false);
        }
      }
    );

    // Initialize auth state
    initializeAuth();

    // Set up periodic session refresh (every 5 minutes)
    const refreshInterval = setInterval(async () => {
      try {
        const { data: { session } } = await directSupabase.auth.getSession();
        if (session) {
          // Refresh the session if it's close to expiring
          const expiresAt = session.expires_at;
          const now = Math.floor(Date.now() / 1000);
          
          // Only refresh if we have an expiration time and it's close to expiring
          if (expiresAt && (expiresAt - now) < 600) {
            console.log('Refreshing session token...');
            const { data, error } = await directSupabase.auth.refreshSession();
            if (error) {
              console.error('Session refresh error:', error);
              setUser(null);
            } else if (data.session) {
              setUser(data.session.user);
            }
          }
        }
      } catch (err) {
        console.error('Session refresh error:', err);
      }
    }, 300000); // Check every 5 minutes

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const result = await directAuth.signIn(email, password);
      if (result.user) {
        setUser(result.user);
      }
    } catch (err) {
      setError(err as AuthError);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setError(null);
      // For now, we'll use signIn since signUp isn't implemented in the direct auth yet
      await directAuth.signIn(email, password);
    } catch (err) {
      setError(err as AuthError);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Clear all caches before signing out
      clearAllCaches();
      
      await directAuth.signOut();
      setUser(null);
      console.log('Successfully signed out');
    } catch (err) {
      console.log('Sign out error:', err);
      // Even if there's an error, clear the local state
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    signIn,
    signOut,
    signUp,
  };

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  );
};

export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
  }
  return context;
}; 